<script lang="ts">
  // Power over time as a filled area, with Xert's MPA line, workout targets and the
  // breakthrough point. Plain charts scale to the power, so MPA, which starts far above it, is
  // clipped at the top and drops into view as it is drawn down; coloured charts scale to all of MPA.
  // `colored` fills it by intensity, like the activity charts (XertAm5 powerFillSeries): grey
  // to blue up to LTP, teal and green up to TP, then yellow, orange and red up to MPA, and
  // always draws the MPA line in the activity chart's purple, and heart rate when `showHr`.
  // Otherwise it is one colour (`color`) fading downwards, with MPA only when `showMpa`.
  import chroma from 'chroma-js'
  import { MEDAL_COLORS } from '../context'
  import type { MedalLevel, ReportData } from '../types'

  export let series: NonNullable<ReportData['series']>
  export let width = 936
  export let height = 260
  export let color = 'var(--accent)'
  export let showMpa = true
  export let showTarget = false
  export let breakthrough: MedalLevel | null = null
  export let mpaColor = 'rgba(255, 255, 255, 0.9)'
  export let targetColor = 'rgba(255, 255, 255, 0.85)'
  export let fillOpacity = 0.45
  export let baseline = true
  // Moving-average radius (in points) for the power line, to calm very spiky rides.
  export let smooth = 2
  export let colored = false
  export let coloredOpacity = 0.92
  export let coloredMpaColor = '#cc33ff'
  export let showHr = false
  export let hrColor = '#ff3b55'

  const uid = Math.random().toString(36).slice(2, 8)

  $: power = smoothed(series.power ?? [], smooth)

  function smoothed(values: (number | null)[], radius: number): (number | null)[] {
    if (radius <= 0) return values
    return values.map((v, i) => {
      if (typeof v !== 'number') return v
      let sum = 0
      let count = 0
      for (let j = Math.max(0, i - radius); j <= Math.min(values.length - 1, i + radius); j++) {
        const w = values[j]
        if (typeof w === 'number') { sum += w; count++ }
      }
      return count ? sum / count : v
    })
  }
  $: n = Math.max(power.length, series.mpa?.length ?? 0, series.target?.length ?? 0)
  $: x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * width)

  // Scale to the 98th percentile of power (or the targets) so one spike doesn't flatten the chart.
  // Coloured charts also make room for the whole MPA line, as the activity chart's shared power
  // axis does, so its dips are drawn where they really are rather than flattened along the top.
  $: yMax = (() => {
    const vals = [...power, ...(showTarget ? series.target ?? [] : [])].filter((v): v is number => typeof v === 'number').sort((a, b) => a - b)
    if (!vals.length) return 100
    const p98 = vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.98))]
    const btPower = series.bt_index !== null && breakthrough ? power[series.bt_index] ?? 0 : 0
    const mpaMax = colored && series.mpa ? Math.max(0, ...series.mpa.filter((v): v is number => typeof v === 'number')) * 1.05 : 0
    const powerMax = Math.max(p98 * 1.15, btPower * 1.15)
    return Math.max(powerMax, mpaMax) || 100
  })()
  $: y = (v: number) => height - (Math.max(0, v) / yMax) * height

  // Reactive so the lines built with it follow x and y when the size or scale changes.
  $: line = (values: (number | null)[], step = false): string => {
    let d = ''
    let open = false
    values.forEach((v, i) => {
      if (typeof v !== 'number') {
        open = false
        return
      }
      if (!open) {
        d += `M${x(i).toFixed(1)},${y(v).toFixed(1)}`
        open = true
      } else if (step) {
        d += `H${x(i).toFixed(1)}V${y(v).toFixed(1)}`
      } else {
        d += `L${x(i).toFixed(1)},${y(v).toFixed(1)}`
      }
    })
    return d
  }

  $: areaPath = power.length ? `M0,${height}` + power.map((v, i) => `L${x(i).toFixed(1)},${y(v ?? 0).toFixed(1)}`).join('') + `L${width},${height}Z` : ''
  $: powerLine = line(power.map((v) => v ?? 0))
  $: mpaLine = (showMpa || colored) && series.mpa ? line(series.mpa) : ''
  $: targetLine = showTarget && series.target ? line(series.target, true) : ''
  // Heart rate on its own scale from zero, as on the activity chart's heart rate axis, with
  // headroom so it stays below MPA along the top edge.
  $: hr = colored && showHr && series.hr ? smoothed(series.hr, smooth) : null
  $: hrMax = hr ? Math.max(0, ...hr.filter((v): v is number => typeof v === 'number')) * 1.15 : 0
  $: hrLine = hr && hrMax ? line(hr.map((v) => (typeof v === 'number' ? (v / hrMax) * yMax : v))) : ''
  // XertAm5's powerGradient colours, stopped at 0, LTP, between LTP and TP, TP, the midpoint
  // to MPA and MPA, which change through the ride.
  const scale = chroma.scale(['bbbbbb', '699cfe', '50e3c2', '79ff4c', 'ffef4c', 'ff7d2a', 'ff0c00'])
  function colorAt(i: number, zones: { ltp: number, ftp: number }): string {
    const p = power[i] ?? 0
    const mpa = series.mmp0s?.[i] ?? series.mpa?.[i] ?? zones.ftp * 2
    const mid = series.mmp_mid?.[i] ?? (zones.ftp + mpa) / 2
    // The stops must rise; MPA can dip close to TP late in a hard ride.
    const stops = [0, zones.ltp, (zones.ltp + zones.ftp) / 2, zones.ftp, mid, mpa]
    for (let k = 1; k < stops.length; k++) stops[k] = Math.max(stops[k], stops[k - 1] + 1)
    return scale.domain(stops)(p).hex()
  }
  $: zoneStops = colored && series.zones && power.length
    ? power.reduce((acc: { offset: number, color: string }[], _, i) => {
      const color = colorAt(i, series.zones!)
      const offset = n <= 1 ? 0 : i / (n - 1)
      // Hard stops between colour changes, as the activity chart draws them.
      if (acc.length && acc[acc.length - 1].color !== color) acc.push({ offset, color: acc[acc.length - 1].color })
      if (!acc.length || acc[acc.length - 1].color !== color) acc.push({ offset, color })
      return acc
    }, [])
    : null

  $: bt = breakthrough && series.bt_index !== null && series.bt_index !== undefined
    ? { x: x(series.bt_index), y: y(power[series.bt_index] ?? 0), color: MEDAL_COLORS[breakthrough].main }
    : null
</script>

<svg {width} {height} viewBox="0 0 {width} {height}" class="xr-chart" aria-hidden="true">
  <defs>
    <linearGradient id="fill-{uid}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" style="stop-color: {color}" stop-opacity={fillOpacity} />
      <stop offset="1" style="stop-color: {color}" stop-opacity="0.02" />
    </linearGradient>
    {#if zoneStops}
      <linearGradient id="zones-{uid}" x1="0" y1="0" x2={width} y2="0" gradientUnits="userSpaceOnUse">
        {#each zoneStops as s}
          <stop offset={s.offset} stop-color={s.color} />
        {/each}
      </linearGradient>
    {/if}
    <clipPath id="clip-{uid}">
      <rect x="0" y="0" {width} {height} />
    </clipPath>
    {#if bt}
      <radialGradient id="bt-{uid}">
        <stop offset="0" stop-color={bt.color} stop-opacity="0.8" />
        <stop offset="1" stop-color={bt.color} stop-opacity="0" />
      </radialGradient>
    {/if}
  </defs>
  <g clip-path="url(#clip-{uid})">
    {#if areaPath}
      {#if zoneStops}
        <path d={areaPath} fill="url(#zones-{uid})" fill-opacity={coloredOpacity} />
        <path d={powerLine} fill="none" stroke="url(#zones-{uid})" stroke-width="2.5" stroke-linejoin="round" />
      {:else}
        <path d={areaPath} fill="url(#fill-{uid})" />
        <path d={powerLine} fill="none" style="stroke: {color}" stroke-width="2.5" stroke-linejoin="round" />
      {/if}
    {/if}
    {#if targetLine}
      <path d={targetLine} fill="none" stroke={targetColor} stroke-width="3" stroke-dasharray="10 7" stroke-linejoin="round" />
    {/if}
    {#if hrLine && zoneStops}
      <path d={hrLine} fill="none" stroke={hrColor} stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
    {/if}
    {#if mpaLine}
      <path d={mpaLine} fill="none" stroke={colored && zoneStops ? coloredMpaColor : mpaColor} stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" />
    {/if}
  </g>
  {#if baseline}
    <line x1="0" y1={height - 1} x2={width} y2={height - 1} stroke="currentColor" stroke-opacity="0.2" stroke-width="2" />
  {/if}
  {#if bt}
    <line x1={bt.x} y1={0} x2={bt.x} y2={height} stroke={bt.color} stroke-width="2" stroke-dasharray="4 6" />
    <circle cx={bt.x} cy={bt.y} r="38" fill="url(#bt-{uid})" />
    <circle cx={bt.x} cy={bt.y} r="11" fill={bt.color} stroke="white" stroke-width="4" />
  {/if}
</svg>

<style>
  .xr-chart {
    display: block;
    overflow: visible;
  }
</style>
